/**
 * Centralized API Configuration (JavaScript version for build tools)
 * Single source of truth for all backend API URLs
 */

import { resolveAppHostname } from './appOrigin.js';
import { usesProductionBackend } from './environmentHostnames.js';

/**
 * Fallback API URL for environments where window is not available
 */
export const DEFAULT_API_URL = 'https://api.meetings.sangoma.com';

/**
 * Development proxy target - the API server to proxy to when running locally
 * This is used by the dev server (rspack/webpack) to proxy /api/* requests
 *
 * NOTE: Local development proxies to the develop environment.
 */
export const DEV_PROXY_TARGET = 'https://api.develop.meetings.sangoma.com';

/**
 * SM-927: Derive the API base URL from a given hostname
 *
 * Patterns supported:
 * 1. *.test.meetings.sangoma.com (ephemeral ticket UI) -> api.develop.meetings.sangoma.com
 * 2. For all other domains (including *.meetings.sangoma.com): prepend 'api.'
 *
 * @param {string} hostname - The hostname to derive the API URL from
 * @returns {string} The API base URL
 */
export function deriveApiUrlFromHostname(hostname) {
  if (hostname.endsWith('.test.meetings.sangoma.com')) {
    return DEV_PROXY_TARGET;
  }
  if (usesProductionBackend(hostname)) {
    return DEFAULT_API_URL;
  }
  return `https://api.${hostname}`;
}

/**
 * Get the base URL for API calls based on dev mode
 * In development (localhost), returns '/api' to use the dev server proxy
 * In production, derives the API URL from the bundle's own origin by prepending 'api.'
 *
 * SM-1183 / SM-1243: the hostname comes from `resolveAppHostname()` (appOrigin.js)
 * so hosted MF mode derives `api.meetings.sangoma.com` from NG Meet's own origin
 * rather than `api.teamhub.sangoma.com` from the host page.
 *
 * @param {boolean} isDevMode - Whether running in development mode (localhost)
 * @returns {string} The base URL to use for API calls
 */
export function getApiBaseUrl(isDevMode) {
  // In development mode, use proxy to avoid CORS issues
  if (isDevMode) {
    return '/api';
  }

  // Derive the API URL from NG Meet's own load origin. In hosted (MF) mode this
  // is the remote's origin, not the host page's (SM-1183).
  const hostname = resolveAppHostname();
  if (hostname) {
    return deriveApiUrlFromHostname(hostname);
  }

  // Fallback for non-browser environments (build tools, SSR, etc.)
  return DEFAULT_API_URL;
}

/**
 * Get the full API endpoint URL
 *
 * @param {string} endpoint - The endpoint path (e.g., '/reservation/app/token')
 * @param {boolean} isDevMode - Whether running in development mode
 * @returns {string} The full URL for the endpoint
 */
export function getApiEndpoint(endpoint, isDevMode) {
  const baseUrl = getApiBaseUrl(isDevMode);
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
}
